import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';

import type User from '@this/domain/user/user';
import { ByMedia } from '@this/shared/ui/other/by_media';
import type { AvailableRepository } from '@this/domain/available_repository';
import SearchResult from '@this/src/domain/search_result';
import type { SelectResponse } from '@this/src/domain/select_repository';
import { Fetcher, HTTPError, objectToFormData, trackClick, trackView, getParams } from '@this/src/util';
import { useShortDistance } from '@this/src/lib/hooks/useShortDistance';
import type SearchQuery from '../../../domain/search_query';
import type SearchQueryItem from '../../../domain/search_query_item';
import SelectRepository from '../../../domain/select_repository';
import type { SelectType, Airline } from '../../../domain/select_store';
import SelectStore from '../../../domain/select_store';
import HotelList from '../../../domain/hotel/hotel_list';

import { SelectContext } from './context/SelectContext';

import SelectPcTemplate from './select_pc.template';
import SelectSpTemplate from './select_sp.template';
import PcShortDistance from './short_distance/PcShortDistance';
import SpShortDistance from './short_distance/SpShortDistance';

import type SearchResultItem from '../../../domain/search_result_item';

const isItemOrHotelLoading = (item: SearchResultItem): boolean => {
  if (item.loading) return true;
  const list = item.elementList;
  if (list == null) return false;
  if (list instanceof HotelList) {
    // ホテルAPI取得中 OR アニメーション中（newId が残っている = 新ホテル未コミット）
    return list.list.some(hotel => hotel.loading) || list.newId !== null;
  }
  // TransitList 等: exit アニメーション中（showBox=false）は新アイテム未コミット
  return !list.showBox;
};

interface SelectProps {
  user: User;
  availableOptions: string[];
  serviceId: number;
  query: SearchQuery;
  availableRepos: AvailableRepository[];
  themeClass: string;
}

const Select: React.FC<SelectProps> = ({
  user,
  availableOptions,
  serviceId,
  query,
  availableRepos,
  themeClass
}) => {
  // React Router Hooks
  const history = useHistory();
  const location = useLocation();
  const [selectErrors, setSelectErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showArrangementRequestForm, setShowArrangementRequestForm] = useState<boolean>(false);
  const [showSearchBox, setShowSearchBox] = useState<boolean>(false);

  const { shortDistance, isLoading: shortDistanceLoading, error: shortDistanceError } = useShortDistance();
  const [additionalSearchErrors, setAdditionalSearchErrors] = useState<{ [k in string]: string } | undefined>(
    undefined
  );
  const [store, setStore] = useState(() => new SelectStore({ query }, user, availableRepos));
  const [repository, setRepository] = useState(() => new SelectRepository({ store }));

  useEffect(() => {
    if (shortDistanceError) {
      setSelectErrors([shortDistanceError]);
    }
  }, [shortDistanceError]);

  useEffect(() => {
    trackView('/select');
    query.applyParams(Object.assign(getParams(), { availableRepos }));
    store.result = new SearchResult({ query });

    // shortDistanceの読み込み完了後に検索を開始
    if (!query.id && !shortDistanceLoading) {
      repository.startSearch(themeClass);
    }
  }, [query, availableRepos, store, repository, themeClass, shortDistanceLoading]);

  useEffect(() => {
    const params = getParams();
    if (params.rebuild === 'true') {
      query.applyParams(Object.assign(params, { availableRepos }));
      store.result = new SearchResult({ query });
      history.replace(location.pathname + location.search.replace(/&?rebuild=true/, ''));
      repository.startSearch(themeClass);
    }
  }, [query, store, repository, themeClass, availableRepos, history, location.pathname, location.search]);

  const handleAddItem = useCallback(
    (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      query.addItem(item_type);
    },
    [query]
  );

  const handleRemoveLastItem = useCallback(() => {
    query.removeItem(query.items.length - 1);
  }, [query]);

  const handleAdditionalSearch = useCallback(
    (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      try {
        query.validationPromise();
        history.push(`/select${query.getQueryString()}`);
        const resultItem = store.result.addItem(queryItem);
        resultItem.setLoading(true);
        repository.startPartialSearch(queryItem);
      } catch (error) {
        setAdditionalSearchErrors(error);
      }
    },
    [query, history, store.result, repository]
  );

  const updateUrl = useCallback(() => {
    let href = '/select';
    href += query.getQueryString();
    if (store.result) {
      href += store.result.getQueryString();
    }
    history.push(href);
    const params = getParams();
    _.map(params.items, (item, i) => {
      if (item.item_type === 'hotel') {
        _.merge(item, {
          prefecture: repository.getPrefecture(Number(i))
        });
      }
      return item;
    });
  }, [query, history, store.result, repository]);

  const handleSelectType = useCallback(
    (type: SelectType) => () => {
      store.result.setType(type);
      updateUrl();
    },
    [store.result, updateUrl]
  );

  const handleAirline = useCallback(
    (airline: Airline) => () => {
      store.result.setType('airPackage');
      store.result.setAirline(airline);
      updateUrl();
    },
    [store.result, updateUrl]
  );

  const handleSelectItem = useCallback(() => {
    setSelectErrors([]);
    updateUrl();
  }, [updateUrl]);

  const updateTransitSegments = useCallback(async () => {
    return Fetcher.put('/update_transit_segments.json', store.result.updateTransitSegmentsParams()).then(
      () => true,
      error => {
        let errorMessages = [];
        if (error instanceof HTTPError && error.response?.data.errors) {
          errorMessages = error.response.data.errors.map((err: { message: string }) => err.message);
        } else {
          errorMessages.push('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
        }
        setSubmitting(false);
        setSelectErrors(errorMessages);
        return false;
      }
    );
  }, [store.result]);

  const handleNextButtonClick = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (submitting) {
        return;
      }
      if (store.result.items.some(item => isItemOrHotelLoading(item))) {
        return;
      }
      trackClick('/select next page button');
      const errors = store.result.errors(query);
      if (errors) {
        setSelectErrors(errors);
        return;
      }

      setSubmitting(true);
      setSelectErrors([]);

      if (store.needReplaceSegments) {
        const segmentUpdated = await updateTransitSegments();
        if (!segmentUpdated) return;
      }

      // APIがFormDataを期待しているため
      Fetcher.post('/reserve_confirm.json', objectToFormData(store.result.confirmParams())).then(
        () => {
          setSubmitting(false);
          history.push(`/reserve_confirm?search_query_id=${query.items[0]?.searchQueryId}`);
        },
        error => {
          let errorMessages = [];
          if (error instanceof HTTPError && error.response?.data.errors) {
            errorMessages = error.response.data.errors;
          } else {
            errorMessages.push('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
          }
          setSubmitting(false);
          setSelectErrors(errorMessages);
        }
      );
    },
    [submitting, store.result, store.needReplaceSegments, query, history, updateTransitSegments]
  );

  const handleReSearch = useCallback(
    (searchQuery: SearchQuery) => {
      searchQuery.clearFlightFilter();
      Fetcher.post('/search_histories', searchQuery.searchHistoryParams());
      if (searchQuery.searchType() === 'rentalCar') {
        Fetcher.post<SelectResponse>(`/select${searchQuery.getQueryString()}`, {}).then(res => {
          history.push(`/reserve_confirm?search_query_id=${res.id}`);
        });
      } else {
        history.push(`/select${searchQuery.getQueryString()}`);

        // Create new store and repository with preserved UI state
        const newStore = new SelectStore({ query: searchQuery }, user, availableRepos);

        // Preserve important UI state from current store
        newStore.currentTab = store.currentTab;
        newStore.packageLoading = store.packageLoading;
        newStore.jrPackageLoading = store.jrPackageLoading;
        newStore.packageErrors = store.packageErrors;
        newStore.jrPackageErrors = store.jrPackageErrors;
        newStore.showPackage = store.showPackage;
        newStore.flashMsgEnd = store.flashMsgEnd;
        newStore.needReplaceSegments = store.needReplaceSegments;

        const newRepository = new SelectRepository({ store: newStore });

        // Update state to trigger re-render
        setStore(newStore);
        setRepository(newRepository);

        newRepository.startSearch(themeClass);
      }
    },
    [history, themeClass, user, availableRepos, store]
  );

  const handleToggleArrangementRequestForm = useCallback(() => {
    setShowArrangementRequestForm(!showArrangementRequestForm);
  }, [showArrangementRequestForm]);

  const toggleShowSearchBox = useCallback(() => {
    setShowSearchBox(!showSearchBox);
  }, [showSearchBox]);

  const handleOpenShowSearchBox = useCallback(() => {
    setShowSearchBox(true);
  }, []);

  const isAnyItemLoading = store.result.items.some(item => isItemOrHotelLoading(item));

  const selectContextValue = useMemo(
    () => ({
      store,
      repository,
      query,
      submitting,
      isAnyItemLoading,
      selectErrors,
      handleNextButtonClick,
      handleSelectType
    }),
    [store, repository, query, submitting, isAnyItemLoading, selectErrors, handleNextButtonClick, handleSelectType]
  );

  return (
    <SelectContext.Provider value={selectContextValue}>
      <div className="select">
        <ByMedia>
          {matches =>
            matches.md || matches.sm ? (
              <div className="select__sp">
                {shortDistance ? (
                  <SpShortDistance
                    showArrangementRequestForm={showArrangementRequestForm}
                    showSearchBox={showSearchBox}
                    additionalSearchErrors={additionalSearchErrors}
                    user={user}
                    availableOptions={availableOptions}
                    serviceId={serviceId}
                    handleAirline={handleAirline}
                    handleSelectItem={handleSelectItem}
                    handleAddItem={handleAddItem}
                    handleAdditionalSearch={handleAdditionalSearch}
                    handleReSearch={handleReSearch}
                    handleToggleArrangementRequestForm={handleToggleArrangementRequestForm}
                    toggleShowSearchBox={toggleShowSearchBox}
                    handleOpenShowSearchBox={handleOpenShowSearchBox}
                  />
                ) : (
                  <SelectSpTemplate
                    showArrangementRequestForm={showArrangementRequestForm}
                    showSearchBox={showSearchBox}
                    additionalSearchErrors={additionalSearchErrors}
                    user={user}
                    availableOptions={availableOptions}
                    serviceId={serviceId}
                    handleAirline={handleAirline}
                    handleSelectItem={handleSelectItem}
                    handleAddItem={handleAddItem}
                    handleAdditionalSearch={handleAdditionalSearch}
                    handleReSearch={handleReSearch}
                    handleToggleArrangementRequestForm={handleToggleArrangementRequestForm}
                    toggleShowSearchBox={toggleShowSearchBox}
                    handleOpenShowSearchBox={handleOpenShowSearchBox}
                  />
                )}
              </div>
            ) : (
              <div className="select__pc" style={{ display: 'flex' }}>
                {shortDistance ? (
                  <PcShortDistance
                    showArrangementRequestForm={showArrangementRequestForm}
                    showSearchBox={showSearchBox}
                    additionalSearchErrors={additionalSearchErrors}
                    user={user}
                    availableOptions={availableOptions}
                    serviceId={serviceId}
                    handleAirline={handleAirline}
                    handleSelectItem={handleSelectItem}
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveLastItem}
                    handleAdditionalSearch={handleAdditionalSearch}
                    handleReSearch={handleReSearch}
                    handleToggleArrangementRequestForm={handleToggleArrangementRequestForm}
                    toggleShowSearchBox={toggleShowSearchBox}
                    handleOpenShowSearchBox={handleOpenShowSearchBox}
                  />
                ) : (
                  <SelectPcTemplate
                    showArrangementRequestForm={showArrangementRequestForm}
                    showSearchBox={showSearchBox}
                    additionalSearchErrors={additionalSearchErrors}
                    user={user}
                    availableOptions={availableOptions}
                    serviceId={serviceId}
                    handleAirline={handleAirline}
                    handleSelectItem={handleSelectItem}
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveLastItem}
                    handleAdditionalSearch={handleAdditionalSearch}
                    handleReSearch={handleReSearch}
                    handleToggleArrangementRequestForm={handleToggleArrangementRequestForm}
                    toggleShowSearchBox={toggleShowSearchBox}
                    handleOpenShowSearchBox={handleOpenShowSearchBox}
                  />
                )}
              </div>
            )
          }
        </ByMedia>
      </div>
    </SelectContext.Provider>
  );
};

export default Select;
